/* Where a click inside a rendered proposal lands, as an offset into the text
 * the island opens on.
 *
 * The note editor asks its preview's own ProseMirror view for this
 * (`posAtCoords`, see SuggestionContentPreview). A questionnaire card cannot:
 * its closed state is rendered HTML, not an editor. The DOM caret APIs answer
 * the same question for rendered text — which text node sits under the pointer,
 * and how far into it — and walking the card's own text up to that node turns
 * it into an offset the island can be focused at.
 *
 * Only a PROPOSED run's text is in the island's document. The replaced text and
 * the line's own label render inside the same card and are not, so neither may
 * shift the offset, and a click on either addresses the position where the
 * proposal begins. */

// The card marks each rendered run with its kind, so this walk can tell proposed
// text from everything else it draws without re-deriving the diff.
const PROPOSED_RUN_SELECTOR =
  '[data-answer-run]:not([data-answer-run="removed"])'

interface CaretPoint {
  node: Node
  offset: number
}

/* `caretPositionFromPoint` is the standard spelling and `caretRangeFromPoint`
 * the older one Chrome still ships; jsdom has neither, so both are probed
 * rather than assumed. */
function caretPointFromPoint(
  clientX: number,
  clientY: number
): CaretPoint | null {
  if (typeof document.caretPositionFromPoint === 'function') {
    const position = document.caretPositionFromPoint(clientX, clientY)
    return position === null
      ? null
      : { node: position.offsetNode, offset: position.offset }
  }
  if (typeof document.caretRangeFromPoint === 'function') {
    const range = document.caretRangeFromPoint(clientX, clientY)
    return range === null
      ? null
      : { node: range.startContainer, offset: range.startOffset }
  }
  return null
}

function isProposed(node: Node): boolean {
  return node.parentElement?.closest(PROPOSED_RUN_SELECTOR) != null
}

/* The click's offset into the proposal `container` renders, or null when the
 * coordinates resolve to nothing inside it — which every caller reads as "no
 * position", leaving the island focused at its end as it was before. */
export function proposalCaretOffsetFromPoint(
  container: HTMLElement,
  clientX: number,
  clientY: number
): number | null {
  const point = caretPointFromPoint(clientX, clientY)
  // An element rather than a text node means the pointer landed on padding
  // between runs, which addresses no character in particular.
  if (
    point === null ||
    point.node.nodeType !== Node.TEXT_NODE ||
    !container.contains(point.node)
  ) {
    return null
  }
  let offset = 0
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const proposed = isProposed(node)
    if (node === point.node) {
      return proposed ? offset + point.offset : offset
    }
    if (proposed) {
      offset += (node.textContent ?? '').length
    }
  }
  return null
}
